import React, { useState, useEffect, useMemo } from 'react';
import { http, getAuthorizationHeader, imageBaseUrl } from '../../config/http';
import Layouts from '../../layouts/Layouts';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import {
  IconBox,
  IconListDetails,
  IconX,
  IconInbox,
  IconCalendarTime,
  IconReload,
  IconChevronLeft,
  IconChevronRight,
  IconAlertTriangle,
  IconChartPie,
  IconUser,
  IconCircleCheck,
  IconPhoto,
  IconSearch,
  IconClock,
  IconBuildingStore,
} from '@tabler/icons-react';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css'; 
import 'react-date-range/dist/theme/default.css';
import moment from 'moment';
import { toast } from 'react-toastify';

const HistoriPermohonanBrankas = () => {
  const [activeTab, setActiveTab] = useState('rombel'); // 'rombel' or 'item'

  // Modal Detail State
  const [isOpenDetailModal, setIsOpenDetailModal] = useState(false);
  const [selectedPermohonan, setSelectedPermohonan] = useState(null);
  const [modalTab, setModalTab] = useState('ringkasan'); // 'ringkasan' | 'list' | 'operator' | 'verifikator' | 'galeri'
  const [searchModalItem, setSearchModalItem] = useState('');

  // Modal Batal State
  const [isOpenBatalModal, setIsOpenBatalModal] = useState(false);
  const [permohonanToBatal, setPermohonanToBatal] = useState(null);
  const [alasanBatal, setAlasanBatal] = useState('');
  const [loadingBatal, setLoadingBatal] = useState(false);

  const handleOpenDetail = async (id) => {
    try {
      const res = await http.get(
        `permohonan-brankas/${id}`,
        { headers: { Authorization: getAuthorizationHeader() } }
      );
      if (res.data.status) {
        setSelectedPermohonan(res.data.data);
        setModalTab('ringkasan');
        setSearchModalItem('');
        setIsOpenDetailModal(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenBatalModal = (permohonan) => {
    setPermohonanToBatal(permohonan);
    setAlasanBatal('');
    setIsOpenBatalModal(true);
  };

  const handleExecuteBatal = async () => {
    if (!permohonanToBatal) return;
    setLoadingBatal(true);
    try {
      const res = await http.post(
        `permohonan-brankas/${permohonanToBatal.id}/batalkan`,
        { alasan: alasanBatal },
        { headers: { Authorization: getAuthorizationHeader() } }
      );
      if (res.data.status) {
        toast.success(res.data.message || 'Permohonan berhasil dibatalkan!');
        setIsOpenBatalModal(false);
        setPermohonanToBatal(null);
        setAlasanBatal('');
        if (isOpenDetailModal) {
          setIsOpenDetailModal(false);
        }
        fetchHistoriRombel();
      } else {
        toast.error(res.data.message || 'Gagal membatalkan permohonan');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Terjadi kesalahan saat membatalkan permohonan');
    } finally {
      setLoadingBatal(false);
    }
  };

  const detailSummary = useMemo(() => {
    if (!selectedPermohonan || !selectedPermohonan.items) return {};
    return selectedPermohonan.items.reduce((acc, item) => {
      const catName = item.jenis_kategori?.kategori?.nama_kategori || 'Tanpa Kategori';
      const karat = item.karat || '-';
      const berat = parseFloat(item.berat || 0);

      if (!acc[catName]) acc[catName] = { name: catName, pcs: 0, berat: 0, byKarat: {} };
      acc[catName].pcs += 1;
      acc[catName].berat += berat;

      if (!acc[catName].byKarat[karat]) acc[catName].byKarat[karat] = { pcs: 0, berat: 0 };
      acc[catName].byKarat[karat].pcs += 1;
      acc[catName].byKarat[karat].berat += berat;

      return acc;
    }, {});
  }, [selectedPermohonan]);

  const resolveItemImageUrl = (item) => {
    if (!item) return '';
    let gData = item.gambar1 || item.gambar;
    if (typeof gData === 'string' && (gData.startsWith('[') || gData.startsWith('{'))) {
      try {
        gData = JSON.parse(gData);
      } catch (e) {}
    }
    if (Array.isArray(gData) && gData.length > 0) {
      const path = gData[0]?.asli || gData[0]?.thumbnail || (typeof gData[0] === 'string' ? gData[0] : '');
      return path ? (path.startsWith('http') ? path : `${imageBaseUrl}${path}`) : '';
    }
    if (typeof gData === 'string' && gData.length > 0) {
      return gData.startsWith('http') ? gData : `${imageBaseUrl}${gData}`;
    }
    return '';
  };

  const filteredModalItems = useMemo(() => {
    if (!selectedPermohonan || !selectedPermohonan.items) return [];
    if (!searchModalItem.trim()) return selectedPermohonan.items;
    const q = searchModalItem.toLowerCase();
    return selectedPermohonan.items.filter((it) => {
      const kode = it.kode_item ? it.kode_item.toLowerCase() : '';
      const jenis = it.jenis_kategori?.nama_jenis ? it.jenis_kategori.nama_jenis.toLowerCase() : '';
      const kat = it.jenis_kategori?.kategori?.nama_kategori ? it.jenis_kategori.kategori.nama_kategori.toLowerCase() : '';
      const baki = it.nomor_rak ? it.nomor_rak.toString().toLowerCase() : '';
      return kode.includes(q) || jenis.includes(q) || kat.includes(q) || baki.includes(q);
    });
  }, [selectedPermohonan, searchModalItem]);

  // Tab 1: Histori Rombel (Permohonan) State
  const [historiRombel, setHistoriRombel] = useState([]);
  const [loadingRombel, setLoadingRombel] = useState(false);
  const [pageRombel, setPageRombel] = useState(1);
  const [lastPageRombel, setLastPageRombel] = useState(1);
  const [totalRombel, setTotalRombel] = useState(0);
  const [pageInputRombel, setPageInputRombel] = useState('1');

  // Tab 2: Histori Pindah (Items) State
  const [historiPindah, setHistoriPindah] = useState([]);
  const [loadingPindah, setLoadingPindah] = useState(false);
  const [pagePindah, setPagePindah] = useState(1);
  const [lastPagePindah, setLastPagePindah] = useState(1);
  const [totalPindah, setTotalPindah] = useState(0);
  const [pageInputPindah, setPageInputPindah] = useState('1');

  useEffect(() => {
    setPageInputRombel(pageRombel.toString());
  }, [pageRombel]);

  useEffect(() => {
    setPageInputPindah(pagePindah.toString());
  }, [pagePindah]);

  const jumpToPageRombel = () => {
    const p = parseInt(pageInputRombel, 10);
    if (!isNaN(p) && p >= 1 && p <= lastPageRombel) {
      setPageRombel(p);
    } else {
      setPageInputRombel(pageRombel.toString());
    }
  };

  const jumpToPagePindah = () => {
    const p = parseInt(pageInputPindah, 10);
    if (!isNaN(p) && p >= 1 && p <= lastPagePindah) {
      setPagePindah(p);
    } else {
      setPageInputPindah(pagePindah.toString());
    }
  };

  // Filter States
  const [startDateRombel, setStartDateRombel] = useState(null);
  const [endDateRombel, setEndDateRombel] = useState(null);
  
  const [startDateItem, setStartDateItem] = useState(null);
  const [endDateItem, setEndDateItem] = useState(null);

  const [isDateOpen, setIsDateOpen] = useState(false);

  const selectionRange = {
    startDate: activeTab === 'rombel' ? (startDateRombel || new Date()) : (startDateItem || new Date()),
    endDate: activeTab === 'rombel' ? (endDateRombel || new Date()) : (endDateItem || new Date()),
    key: 'selection',
  };

  const handleSelectDate = (date) => {
    if (activeTab === 'rombel') {
      setStartDateRombel(date.selection.startDate);
      setEndDateRombel(date.selection.endDate);
      setPageRombel(1);
    } else {
      setStartDateItem(date.selection.startDate);
      setEndDateItem(date.selection.endDate);
      setPagePindah(1);
    }
  };

  const fetchHistoriRombel = async () => {
    setLoadingRombel(true);
    try {
      let url = `permohonan-brankas?page=${pageRombel}&per_page=10`;
      if (startDateRombel && endDateRombel) {
        url += `&start_date=${moment(startDateRombel).format('YYYY-MM-DD')}&end_date=${moment(endDateRombel).format('YYYY-MM-DD')}`;
      }
      const res = await http.get(url, { headers: { Authorization: getAuthorizationHeader() } });
      if (res.data.status) {
        setHistoriRombel(res.data.data.data || []);
        setLastPageRombel(res.data.data.last_page || 1);
        setTotalRombel(res.data.data.total || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingRombel(false);
    }
  };

  const fetchHistoriPindah = async () => {
    setLoadingPindah(true);
    try {
      let url = `permohonan-brankas/histori-pindah?page=${pagePindah}&per_page=10`;
      if (startDateItem && endDateItem) {
        url += `&start_date=${moment(startDateItem).format('YYYY-MM-DD')}&end_date=${moment(endDateItem).format('YYYY-MM-DD')}`;
      }
      const res = await http.get(url, { headers: { Authorization: getAuthorizationHeader() } });
      if (res.data.status) {
        setHistoriPindah(res.data.data.data || []);
        setLastPagePindah(res.data.data.last_page || 1);
        setTotalPindah(res.data.data.total || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingPindah(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'rombel') {
      fetchHistoriRombel();
    } else {
      fetchHistoriPindah();
    }
  }, [pageRombel, pagePindah, activeTab, startDateRombel, endDateRombel, startDateItem, endDateItem]);

  return (
    <Layouts title="Histori Ke Pusat">
      <div className="p-4 sm:p-6 w-full space-y-6 min-h-[75vh]">
        
        {/* Tabs Navigation & Filters */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-200 dark:border-slate-700 pb-0">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveTab('rombel')}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'rombel'
                  ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              <IconBox size={18} /> Pemindahan
            </button>
            <button
              onClick={() => setActiveTab('item')}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'item'
                  ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              <IconListDetails size={18} /> Item Barang
            </button>
          </div>
          
          <div className="flex items-center gap-2 relative mb-2 md:mb-0">
            <button
              onClick={() => setIsDateOpen(!isDateOpen)}
              className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-2 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 transition-colors"
            >
              <IconCalendarTime size={16} />
              {activeTab === 'rombel' 
                ? (startDateRombel && endDateRombel ? `${moment(startDateRombel).format('DD/MM/YYYY')} - ${moment(endDateRombel).format('DD/MM/YYYY')}` : 'Filter Tanggal')
                : (startDateItem && endDateItem ? `${moment(startDateItem).format('DD/MM/YYYY')} - ${moment(endDateItem).format('DD/MM/YYYY')}` : 'Filter Tanggal')
              }
            </button>
            {((activeTab === 'rombel' && startDateRombel) || (activeTab === 'item' && startDateItem)) && (
              <button
                onClick={() => { 
                  if (activeTab === 'rombel') { setStartDateRombel(null); setEndDateRombel(null); setPageRombel(1); }
                  else { setStartDateItem(null); setEndDateItem(null); setPagePindah(1); }
                }}
                className="p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors"
                title="Reset Filter"
              >
                <IconReload size={16} />
              </button>
            )}

            {isDateOpen && (
              <div className="absolute right-0 top-full mt-2 z-50 bg-white rounded-xl shadow-xl border border-slate-200 p-2">
                <div className="flex justify-between items-center mb-2 px-2">
                  <span className="text-sm font-bold">Pilih Rentang Tanggal</span>
                  <button onClick={() => setIsDateOpen(false)} className="text-slate-400 hover:text-slate-600">
                    <IconX size={16} />
                  </button>
                </div>
                <DateRange
                  ranges={[selectionRange]}
                  onChange={handleSelectDate}
                  rangeColors={['#f59e0b']}
                />
              </div>
            )}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          
          {/* TAB 1: Rombel */}
          {activeTab === 'rombel' && (
            <div className="overflow-x-auto">
              {loadingRombel ? (
                <div className="py-16 flex flex-col items-center justify-center gap-3">
                  <span className="loading loading-spinner loading-lg text-amber-500"></span>
                  <p className="text-slate-400 text-xs animate-pulse">Memuat histori pemindahan...</p>
                </div>
              ) : historiRombel.length === 0 ? (
                <div className="py-16 flex flex-col items-center justify-center text-slate-400 text-xs gap-3">
                  <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-full animate-bounce">
                    <IconInbox size={32} className="text-slate-300 dark:text-slate-500" stroke={1.5} />
                  </div>
                  <span>Belum ada histori pemindahan.</span>
                </div>
              ) : (
                <>
                  <table className="w-full text-xs text-left text-slate-600 dark:text-slate-300">
                    <thead className="text-[11px] uppercase bg-gradient-to-r from-amber-50 via-amber-100/80 to-amber-50 dark:from-amber-900/30 dark:via-amber-800/20 dark:to-amber-900/30 text-amber-800 dark:text-amber-300 border-b border-amber-200/70 dark:border-amber-800/40 font-bold tracking-wider">
                      <tr>
                        <th className="p-3.5 w-10 font-bold">No.</th>
                        <th className="p-3.5 font-bold">Waktu Request</th>
                        <th className="p-3.5 font-bold">Tgl Terima</th>
                        <th className="p-3.5 font-bold">No. Request</th>
                        <th className="p-3.5 font-bold">Pemohon</th>
                        <th className="p-3.5 font-bold">Verifikator</th>
                        <th className="p-3.5 text-center font-bold">Total Barang</th>
                        <th className="p-3.5 font-bold">Status</th>
                        <th className="p-3.5 text-center font-bold">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                      {historiRombel.map((h, idx) => (
                        <tr key={h.id} onClick={() => handleOpenDetail(h.id)} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer">
                          <td className="p-3.5 text-slate-500 font-medium">{(pageRombel - 1) * 10 + idx + 1}.</td>
                          <td className="p-3.5 text-slate-500">{new Date(h.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                          <td className="p-3.5 text-slate-500 font-medium">
                            {h.status !== 'pending' && h.updated_at 
                              ? new Date(h.updated_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) 
                              : '-'}
                          </td>
                          <td className="p-3.5 font-mono font-bold text-amber-600 dark:text-amber-400">{h.no_permohonan}</td>
                          <td className="p-3.5 text-slate-700 dark:text-slate-300">{h.user_pemohon?.name || h.userPemohon?.name}</td>
                          <td className="p-3.5 font-semibold text-emerald-600 dark:text-emerald-400">{h.user_verifikator?.name || h.userVerifikator?.name || '-'}</td>
                          <td className="p-3.5 text-center font-bold text-slate-700 dark:text-slate-200">
                            {h.total_pcs || h.total_items || (h.items ? h.items.length : 0)} PCS / {parseFloat(h.total_berat || (h.items ? h.items.reduce((acc, it) => acc + parseFloat(it.berat || 0), 0) : 0)).toFixed(3)} gr
                          </td>
                          <td className="p-3.5">
                            {h.status === 'pending' ? (
                              <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-bold">Pending</span>
                            ) : h.status === 'disetujui' ? (
                              <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-bold">Disetujui</span>
                            ) : h.status === 'dibatalkan' ? (
                              <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded text-xs font-bold">Dibatalkan</span>
                            ) : (
                              <span className="px-2 py-1 bg-rose-100 text-rose-700 rounded text-xs font-bold">Ditolak</span>
                            )}
                          </td>
                          <td className="p-3.5 text-center" onClick={(e) => e.stopPropagation()}>
                            {h.status === 'pending' ? (
                              <button
                                onClick={() => handleOpenBatalModal(h)}
                                className="px-2.5 py-1 text-xs font-semibold text-rose-600 dark:text-rose-400 hover:text-white hover:bg-rose-600 border border-rose-200 dark:border-rose-800/50 hover:border-rose-600 rounded-lg transition-all shadow-sm flex items-center gap-1 mx-auto cursor-pointer"
                                title="Batalkan Permohonan"
                              >
                                <IconX size={14} />
                                <span>Batalkan</span>
                              </button>
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-3 p-3.5 border-t border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      Menampilkan <span className="font-semibold text-slate-700 dark:text-slate-200">{historiRombel.length}</span> dari <span className="font-semibold text-slate-700 dark:text-slate-200">{totalRombel}</span> permohonan
                    </span>
                    <div className="flex items-center gap-2.5">
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                        Halaman
                        <input
                          type="text"
                          value={pageInputRombel}
                          onChange={(e) => setPageInputRombel(e.target.value)}
                          onBlur={jumpToPageRombel}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              jumpToPageRombel();
                              e.target.blur();
                            }
                          }}
                          className="w-12 h-7 text-center border border-slate-200 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-amber-700 dark:text-amber-400 font-bold focus:border-amber-400 focus:ring-1 focus:ring-amber-200 outline-none transition-all"
                        />
                        dari <span className="font-bold text-amber-700 dark:text-amber-400">{lastPageRombel}</span>
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setPageRombel((prev) => Math.max(1, prev - 1))}
                          disabled={pageRombel <= 1}
                          title="previous"
                          type="button"
                          className="inline-flex items-center justify-center w-8 h-8 border border-slate-200 dark:border-slate-600 rounded-lg shadow-sm hover:bg-amber-50 dark:hover:bg-slate-800 hover:border-amber-300 dark:hover:border-amber-600 hover:text-amber-600 dark:hover:text-amber-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                        >
                          <IconChevronLeft size={16} />
                        </button>
                        <button
                          onClick={() => setPageRombel((prev) => Math.min(lastPageRombel, prev + 1))}
                          disabled={pageRombel >= lastPageRombel}
                          title="next"
                          type="button"
                          className="inline-flex items-center justify-center w-8 h-8 border border-slate-200 dark:border-slate-600 rounded-lg shadow-sm hover:bg-amber-50 dark:hover:bg-slate-800 hover:border-amber-300 dark:hover:border-amber-600 hover:text-amber-600 dark:hover:text-amber-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                        >
                          <IconChevronRight size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* TAB 2: Items */}
          {activeTab === 'item' && (
            <div className="overflow-x-auto">
              {loadingPindah ? (
                <div className="py-16 flex flex-col items-center justify-center gap-3">
                  <span className="loading loading-spinner loading-lg text-amber-500"></span>
                  <p className="text-slate-400 text-xs animate-pulse">Memuat histori pemindahan item...</p>
                </div>
              ) : historiPindah.length === 0 ? (
                <div className="py-16 flex flex-col items-center justify-center text-slate-400 text-xs gap-3">
                  <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-full animate-bounce">
                    <IconInbox size={32} className="text-slate-300 dark:text-slate-500" stroke={1.5} />
                  </div>
                  <span>Belum ada histori pemindahan item ke Brankas Pusat.</span>
                </div>
              ) : (
                <>
                  <table className="w-full text-xs text-left text-slate-600 dark:text-slate-300">
                    <thead className="text-[11px] uppercase bg-gradient-to-r from-amber-50 via-amber-100/80 to-amber-50 dark:from-amber-900/30 dark:via-amber-800/20 dark:to-amber-900/30 text-amber-800 dark:text-amber-300 border-b border-amber-200/70 dark:border-amber-800/40 font-bold tracking-wider">
                      <tr>
                        <th className="p-3.5 w-10 font-bold">No.</th>
                        <th className="p-3.5 font-bold">Waktu Request</th>
                        <th className="p-3.5 font-bold">No. Request</th>
                        <th className="p-3.5 font-bold">Kode Barang</th>
                        <th className="p-3.5 font-bold">Nama Barang</th>
                        <th className="p-3.5 font-bold">Brankas Asal - Tujuan</th>
                        <th className="p-3.5 font-bold">Pemohon</th>
                        <th className="p-3.5 font-bold">Verifikator (Admin)</th>
                        <th className="p-3.5 font-bold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                      {historiPindah.map((h, idx) => (
                        <tr key={h.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                          <td className="p-3.5 text-slate-500 font-medium">{(pagePindah - 1) * 10 + idx + 1}.</td>
                          <td className="p-3.5 text-slate-500">{new Date(h.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                          <td className="p-3.5 font-mono font-bold text-amber-600 dark:text-amber-400">{h.permohonan?.no_permohonan}</td>
                          <td className="p-3.5 font-mono font-bold text-slate-800 dark:text-slate-100">{h.item?.kode_item}</td>
                          <td className="p-3.5">{h.item?.jenis_kategori?.nama_jenis}</td>
                          <td className="p-3.5">
                            <span className="font-semibold text-slate-700 dark:text-slate-200">{h.permohonan?.cabang_asal?.nama_cabang || h.permohonan?.cabangAsal?.nama_cabang}</span>
                            <span className="text-amber-500 mx-1 font-bold">➔</span>
                            <span className="font-bold text-emerald-600 dark:text-emerald-400">Brankas Pusat</span>
                          </td>
                          <td className="p-3.5 text-slate-700 dark:text-slate-300">{h.permohonan?.user_pemohon?.name || h.permohonan?.userPemohon?.name}</td>
                          <td className="p-3.5 font-bold text-emerald-700 dark:text-emerald-300">{h.permohonan?.user_verifikator?.name || h.permohonan?.userVerifikator?.name || '-'}</td>
                          <td className="p-3.5">
                            {h.status_item === 'pending' ? (
                              <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-bold">Pending</span>
                            ) : h.status_item === 'disetujui' ? (
                              <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-bold">Disetujui</span>
                            ) : h.status_item === 'dibatalkan' ? (
                              <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded text-xs font-bold">Dibatalkan</span>
                            ) : (
                              <span className="px-2 py-1 bg-rose-100 text-rose-700 rounded text-xs font-bold">Ditolak</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-3 p-3.5 border-t border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      Menampilkan <span className="font-semibold text-slate-700 dark:text-slate-200">{historiPindah.length}</span> dari <span className="font-semibold text-slate-700 dark:text-slate-200">{totalPindah}</span> item barang
                    </span>
                    <div className="flex items-center gap-2.5">
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                        Halaman
                        <input
                          type="text"
                          value={pageInputPindah}
                          onChange={(e) => setPageInputPindah(e.target.value)}
                          onBlur={jumpToPagePindah}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              jumpToPagePindah();
                              e.target.blur();
                            }
                          }}
                          className="w-12 h-7 text-center border border-slate-200 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-amber-700 dark:text-amber-400 font-bold focus:border-amber-400 focus:ring-1 focus:ring-amber-200 outline-none transition-all"
                        />
                        dari <span className="font-bold text-amber-700 dark:text-amber-400">{lastPagePindah}</span>
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setPagePindah((prev) => Math.max(1, prev - 1))}
                          disabled={pagePindah <= 1}
                          title="previous"
                          type="button"
                          className="inline-flex items-center justify-center w-8 h-8 border border-slate-200 dark:border-slate-600 rounded-lg shadow-sm hover:bg-amber-50 dark:hover:bg-slate-800 hover:border-amber-300 dark:hover:border-amber-600 hover:text-amber-600 dark:hover:text-amber-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                        >
                          <IconChevronLeft size={16} />
                        </button>
                        <button
                          onClick={() => setPagePindah((prev) => Math.min(lastPagePindah, prev + 1))}
                          disabled={pagePindah >= lastPagePindah}
                          title="next"
                          type="button"
                          className="inline-flex items-center justify-center w-8 h-8 border border-slate-200 dark:border-slate-600 rounded-lg shadow-sm hover:bg-amber-50 dark:hover:bg-slate-800 hover:border-amber-300 dark:hover:border-amber-600 hover:text-amber-600 dark:hover:text-amber-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                        >
                          <IconChevronRight size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

        </div>

        {/* Detail Modal */}
        {isOpenDetailModal && selectedPermohonan && (
          <div className="fixed inset-0 z-[999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setIsOpenDetailModal(false)}>
            <div
              className="bg-white dark:bg-slate-800 w-full max-w-4xl lg:max-w-5xl rounded-md p-5 md:p-6 shadow-2xl border border-slate-200 dark:border-slate-700 max-h-[92vh] flex flex-col space-y-4"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-3 flex-shrink-0">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-sm md:text-base text-slate-800 dark:text-slate-100">
                      Detail Permohonan: <span className="font-mono text-amber-600">{selectedPermohonan.no_permohonan}</span>
                    </h3>
                    {selectedPermohonan.status === 'pending' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 rounded-md border border-amber-300 dark:border-amber-700 text-[11px] font-bold">Pending</span>
                    ) : selectedPermohonan.status === 'disetujui' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 rounded-md border border-emerald-300 dark:border-emerald-700 text-[11px] font-bold">✓ Disetujui</span>
                    ) : selectedPermohonan.status === 'dibatalkan' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-md border border-slate-300 text-[11px] font-bold">Dibatalkan</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 rounded-md border border-rose-300 dark:border-rose-700 text-[11px] font-bold">✕ Ditolak</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 mt-1">
                    <span><strong className="text-slate-600 dark:text-slate-400">Request:</strong> {new Date(selectedPermohonan.created_at).toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    {selectedPermohonan.status !== 'pending' && selectedPermohonan.updated_at && (
                      <span><strong className="text-slate-600 dark:text-slate-400">Verifikasi:</strong> {new Date(selectedPermohonan.updated_at).toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsOpenDetailModal(false)}
                  className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors cursor-pointer"
                >
                  <IconX size={20} />
                </button>
              </div>

              {/* Top Quick Info Strip */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs bg-slate-50 dark:bg-slate-700/40 p-3 rounded-xl flex-shrink-0">
                <div>
                  <span className="text-slate-400 block text-[10px]">Toko Asal (Brankas)</span>
                  <span className="font-bold text-slate-800 dark:text-slate-200 truncate block">
                    {selectedPermohonan.cabang_asal?.nama_cabang ? selectedPermohonan.cabang_asal.nama_cabang.replace(/Etalase/gi, 'Brankas') : '-'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px]">Operator Pemohon</span>
                  <span className="font-bold text-slate-800 dark:text-slate-200 truncate block">
                    {selectedPermohonan.user_pemohon?.name || selectedPermohonan.userPemohon?.name || '-'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px]">Total Item & Berat</span>
                  <span className="font-bold text-slate-800 dark:text-slate-200 truncate block">
                    {selectedPermohonan.total_pcs || (selectedPermohonan.items ? selectedPermohonan.items.length : 0)} PCS
                    <span className="text-amber-600 dark:text-amber-400 font-semibold ml-1">
                      ({parseFloat(selectedPermohonan.total_berat || (selectedPermohonan.items ? selectedPermohonan.items.reduce((acc, it) => acc + parseFloat(it.berat || 0), 0) : 0)).toFixed(3)} gr)
                    </span>
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px]">Verifikator</span>
                  <span className="font-bold text-emerald-600 dark:text-emerald-400 truncate block">
                    {selectedPermohonan.user_verifikator?.name || selectedPermohonan.userVerifikator?.name || (selectedPermohonan.status === 'pending' ? 'Menunggu...' : '-')}
                  </span>
                </div>
              </div>

              {/* 5 Tab Navigation Bar */}
              <div className="flex border-b border-slate-200 dark:border-slate-700 gap-1 overflow-x-auto scrollbar-none flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setModalTab('ringkasan')}
                  className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                    modalTab === 'ringkasan'
                      ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                      : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                  }`}
                >
                  <IconChartPie size={16} />
                  <span>Ringkasan Item</span>
                </button>
                <button
                  type="button"
                  onClick={() => setModalTab('list')}
                  className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                    modalTab === 'list'
                      ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                      : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                  }`}
                >
                  <IconListDetails size={16} />
                  <span>List Item ({selectedPermohonan.items?.length || 0})</span>
                </button>
                <button
                  type="button"
                  onClick={() => setModalTab('operator')}
                  className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                    modalTab === 'operator'
                      ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                      : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                  }`}
                >
                  <IconUser size={16} />
                  <span>Operator Pemohon</span>
                </button>
                <button
                  type="button"
                  onClick={() => setModalTab('verifikator')}
                  className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                    modalTab === 'verifikator'
                      ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                      : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                  }`}
                >
                  <IconCircleCheck size={16} />
                  <span>Verifikator</span>
                </button>
                <button
                  type="button"
                  onClick={() => setModalTab('galeri')}
                  className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                    modalTab === 'galeri'
                      ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                      : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                  }`}
                >
                  <IconPhoto size={16} />
                  <span>Galeri List Item</span>
                </button>
              </div>

              {/* Scrollable Tab Content */}
              <div className="flex-1 overflow-y-auto pr-1">
                {/* 1. Tab Ringkasan Item */}
                {modalTab === 'ringkasan' && (() => {
                  const itemsList = selectedPermohonan.items || [];
                  const totalPcsVal = selectedPermohonan.total_pcs || itemsList.length;
                  const totalBeratVal = parseFloat(selectedPermohonan.total_berat || itemsList.reduce((acc, it) => acc + parseFloat(it.berat || it.item?.berat || 0), 0));
                  const avgBeratVal = itemsList.length > 0 ? (totalBeratVal / itemsList.length) : (totalPcsVal > 0 ? totalBeratVal / totalPcsVal : 0);
                  const validModalItems = itemsList.filter(it => Number(it.harga_modal || it.item?.harga_modal || 0) > 0);
                  const avgModalVal = validModalItems.length > 0
                    ? Math.round(validModalItems.reduce((acc, it) => acc + Number(it.harga_modal || it.item?.harga_modal || 0), 0) / validModalItems.length)
                    : 0;
                  const maxBeratVal = itemsList.length > 0 ? Math.max(...itemsList.map(it => parseFloat(it.berat || it.item?.berat || 0) || 0)) : 0;
                  const maxModalVal = itemsList.length > 0 ? Math.max(...itemsList.map(it => Number(it.harga_modal || it.item?.harga_modal || 0) || 0)) : 0;

                  return (
                    <div className="space-y-4">
                      {/* Ringkasan Cards (7 Cards) */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3">
                        <div className="bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-900/40 rounded-xl p-3">
                          <span className="text-amber-700 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider block">Total Barang</span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                            {totalPcsVal} <span className="text-xs font-semibold text-slate-500">Pcs</span>
                          </div>
                        </div>
                        <div className="bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-900/40 rounded-xl p-3">
                          <span className="text-amber-700 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider block">Total Berat</span>
                          <div className="text-lg font-extrabold text-amber-600 dark:text-amber-400 mt-0.5 font-mono">
                            {totalBeratVal.toFixed(3)} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Total Kategori</span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                            {Object.keys(detailSummary).length} <span className="text-xs font-semibold text-slate-500">Kategori</span>
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Rata-rata Berat</span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                            {avgBeratVal.toFixed(3)} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Rata-rata Modal</span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                            Rp {avgModalVal.toLocaleString('id-ID')}
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Berat Maksimal</span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                            {maxBeratVal.toFixed(3)} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Modal Maksimal</span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                            Rp {maxModalVal.toLocaleString('id-ID')}
                          </div>
                        </div>
                      </div>

                    {/* Breakdown Kategori & Karat */}
                    {Object.keys(detailSummary).length > 0 && (
                      <div className="space-y-3 p-3.5 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-900/30 rounded-xl">
                        <div className="flex items-center justify-between">
                          <h4 className="text-[11px] font-bold text-amber-800 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                            <IconChartPie size={16} />
                            <span>Breakdown Per Kategori & Karat</span>
                          </h4>
                          <span className="text-[10px] text-amber-700 dark:text-amber-400 font-semibold">
                            {Object.keys(detailSummary).length} Kategori
                          </span>
                        </div>
                        <div className="space-y-2">
                          {Object.values(detailSummary).map((cat, idx) => (
                            <div key={idx} className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-amber-200/60 dark:border-slate-700 shadow-sm">
                              <div className="flex justify-between items-center mb-2 pb-2 border-b border-slate-100 dark:border-slate-700">
                                <span className="font-bold text-slate-800 dark:text-slate-100 text-sm">{cat.name}</span>
                                <span className="font-bold text-amber-600 dark:text-amber-400 text-xs bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-2.5 py-0.5 rounded-md">
                                  {cat.pcs} Pcs ({cat.berat.toFixed(3)} gr)
                                </span>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                {Object.entries(cat.byKarat).map(([karat, val], i) => (
                                  <div key={i} className="flex items-center justify-between bg-slate-50 dark:bg-slate-700/40 px-2.5 py-1.5 rounded-lg text-xs border border-slate-100 dark:border-slate-700">
                                    <span className="font-semibold text-slate-500 dark:text-slate-400">{karat} Karat</span>
                                    <span className="font-bold text-slate-800 dark:text-slate-100">{val.pcs} Pcs / {val.berat.toFixed(3)}g</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Catatan Area */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                      <div className="p-3 bg-slate-50 dark:bg-slate-700/30 rounded-xl border border-slate-200 dark:border-slate-700 space-y-1">
                        <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider block">Catatan Pemohon</span>
                        <p className="text-xs text-slate-700 dark:text-slate-300 font-medium">
                          {selectedPermohonan.catatan_pemohon || <span className="italic text-slate-400">Tidak ada catatan</span>}
                        </p>
                      </div>
                      <div className="p-3 bg-slate-50 dark:bg-slate-700/30 rounded-xl border border-slate-200 dark:border-slate-700 space-y-1">
                        <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider block">
                          {selectedPermohonan.status === 'dibatalkan' ? 'Alasan Pembatalan' : 'Catatan Verifikator'}
                        </span>
                        <p className="text-xs text-slate-700 dark:text-slate-300 font-medium">
                          {((selectedPermohonan.catatan_verifikasi || selectedPermohonan.catatan_verifikator || selectedPermohonan.alasan_batal)
                            ? (selectedPermohonan.catatan_verifikasi || selectedPermohonan.catatan_verifikator || selectedPermohonan.alasan_batal).replace(/^\[BATAL\]\s*/i, '')
                            : null) || (
                            <span className="italic text-slate-400">
                              {selectedPermohonan.status === 'pending' ? 'Menunggu verifikasi...' : 'Tidak ada catatan'}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}

                {/* 2. Tab List Item (Tabel) */}
                {modalTab === 'list' && (
                  <div className="space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="relative flex-1 max-w-xs">
                        <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                        <input
                          type="text"
                          placeholder="Cari kode, jenis, baki..."
                          value={searchModalItem}
                          onChange={(e) => setSearchModalItem(e.target.value)}
                          className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200"
                        />
                      </div>
                      <span className="text-[11px] text-slate-500 font-medium">
                        Menampilkan {filteredModalItems.length} dari {selectedPermohonan.items?.length || 0} item
                      </span>
                    </div>

                    <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-x-auto max-h-80">
                      <table className="w-full text-xs text-left text-slate-600 dark:text-slate-300">
                        <thead className="bg-gradient-to-r from-amber-50 via-amber-100/80 to-amber-50 dark:from-amber-900/40 dark:via-amber-800/30 dark:to-amber-900/40 text-[11px] text-amber-800 dark:text-amber-300 uppercase border-b border-amber-200/70 dark:border-amber-800/40 sticky top-0 z-10 font-bold tracking-wider">
                          <tr>
                            <th className="p-2.5 w-10 text-center font-bold">No</th>
                            <th className="p-2.5 font-bold">Kode Barang</th>
                            <th className="p-2.5 font-bold">Nama Barang</th>
                            <th className="p-2.5 text-center font-bold">Baki / Rak</th>
                            <th className="p-2.5 text-right font-bold">Berat (gr)</th>
                            <th className="p-2.5 text-center font-bold">Karat</th>
                            <th className="p-2.5 text-center font-bold">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                          {filteredModalItems.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="text-center py-6 text-slate-400">
                                Tidak ada item yang sesuai dengan pencarian
                              </td>
                            </tr>
                          ) : (
                            filteredModalItems.map((it, idx) => (
                              <tr key={it.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                                <td className="p-2.5 text-slate-500 font-medium text-center">{idx + 1}.</td>
                                <td className="p-2.5 font-mono font-bold text-amber-600 dark:text-amber-400">{it.kode_item}</td>
                                <td className="p-2.5">
                                  <span className="text-slate-600 dark:text-slate-400">{it.jenis_kategori?.nama_jenis || '-'}</span>
                                </td>
                                <td className="p-2.5 text-center font-medium">{it.nomor_rak || '-'}</td>
                                <td className="p-2.5 text-right font-bold text-amber-600 dark:text-amber-400">{parseFloat(it.berat).toFixed(3)} gr</td>
                                <td className="p-2.5 text-center font-semibold">{it.karat} K</td>
                                <td className="p-2.5 text-center">
                                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                    it.pivot?.status_item === 'disetujui' || selectedPermohonan.status === 'disetujui'
                                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300'
                                      : it.pivot?.status_item === 'ditolak' || selectedPermohonan.status === 'ditolak'
                                      ? 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300'
                                      : it.pivot?.status_item === 'dibatalkan' || selectedPermohonan.status === 'dibatalkan'
                                      ? 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                                      : 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300'
                                  }`}>
                                    {it.pivot?.status_item || selectedPermohonan.status}
                                  </span>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                        {filteredModalItems.length > 0 && (
                          <tfoot className="bg-slate-50 dark:bg-slate-700/50 font-bold border-t border-slate-200 dark:border-slate-700 sticky bottom-0">
                            <tr>
                              <td colSpan={4} className="p-2.5 text-right text-slate-600 dark:text-slate-300">
                                Total ({filteredModalItems.length} item):
                              </td>
                              <td className="p-2.5 text-right text-amber-600 dark:text-amber-400 font-mono">
                                {filteredModalItems.reduce((acc, it) => acc + parseFloat(it.berat || 0), 0).toFixed(3)} gr
                              </td>
                              <td colSpan={2}></td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </div>
                )}

                {/* 3. Tab Operator Pemohon */}
                {modalTab === 'operator' && (() => {
                  const userPemohon = selectedPermohonan.user_pemohon || selectedPermohonan.userPemohon;
                  return (
                    <div className="space-y-4">
                      <div className="bg-slate-50 dark:bg-slate-700/30 p-5 rounded-2xl border border-slate-200 dark:border-slate-700">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b border-slate-200 dark:border-slate-600">
                          <div className="flex items-center gap-3.5">
                            <div className="w-12 h-12 rounded-2xl bg-amber-500/10 dark:bg-amber-400/20 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold text-lg border border-amber-200 dark:border-amber-700/50">
                              {userPemohon?.name ? userPemohon.name.charAt(0).toUpperCase() : <IconUser size={24} />}
                            </div>
                            <div>
                              <h4 className="font-bold text-base text-slate-800 dark:text-slate-100">
                                {userPemohon?.name || 'Operator Rumah'}
                              </h4>
                              {userPemohon?.email && (
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                  {userPemohon.email}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2.5 py-1 text-[11px] font-bold rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                              {userPemohon?.roles?.map((r) => r.name.replace(/_/g, ' ').toUpperCase()).join(', ') || 'OPERATOR RUMAH'}
                            </span>
                            <span className={`px-2.5 py-1 text-[11px] font-bold rounded-lg border ${
                              userPemohon?.is_active !== false && userPemohon?.is_active !== 0
                                ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 border-slate-200'
                            }`}>
                              {userPemohon?.is_active !== false && userPemohon?.is_active !== 0 ? '● Akun Aktif' : '○ Non-Aktif'}
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pt-4 text-xs">
                          <div className="space-y-1">
                            <span className="text-slate-400 text-[11px] block">Cabang / Brankas Asal</span>
                            <div className="flex items-center gap-1.5 font-bold text-slate-800 dark:text-slate-200">
                              <IconBuildingStore size={15} className="text-amber-500" />
                              <span>{selectedPermohonan.cabang_asal?.nama_cabang ? selectedPermohonan.cabang_asal.nama_cabang.replace(/Etalase/gi, 'Brankas') : (userPemohon?.cabang?.nama_cabang || '-')}</span>
                            </div>
                          </div>

                          <div className="space-y-1">
                            <span className="text-slate-400 text-[11px] block">Lokasi Toko</span>
                            <p className="font-semibold text-slate-700 dark:text-slate-300">
                              {selectedPermohonan.cabang_asal?.lokasi || userPemohon?.cabang?.lokasi || '-'}
                            </p>
                          </div>

                          <div className="space-y-1">
                            <span className="text-slate-400 text-[11px] block">Waktu Pengajuan Permohonan</span>
                            <div className="flex items-center gap-1.5 font-semibold text-slate-700 dark:text-slate-300">
                              <IconClock size={15} className="text-slate-400" />
                              <span>{moment(selectedPermohonan.created_at).format('DD MMM YYYY, HH:mm')}</span>
                            </div>
                          </div>

                          <div className="space-y-1 sm:col-span-2 md:col-span-3 pt-2 border-t border-slate-200 dark:border-slate-600">
                            <span className="text-slate-400 text-[11px] block">Catatan yang Dilampirkan Pemohon</span>
                            <div className="p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-medium leading-relaxed">
                              {selectedPermohonan.catatan_pemohon || <span className="italic text-slate-400">Tidak ada catatan khusus dari pemohon.</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* 4. Tab Verifikator */}
                {modalTab === 'verifikator' && (() => {
                  const userVerifikator = selectedPermohonan.user_verifikator || selectedPermohonan.userVerifikator;
                  return (
                    <div className="space-y-4">
                      {userVerifikator ? (
                        <div className="bg-slate-50 dark:bg-slate-700/30 p-5 rounded-2xl border border-slate-200 dark:border-slate-700">
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b border-slate-200 dark:border-slate-600">
                            <div className="flex items-center gap-3.5">
                              <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 dark:bg-emerald-400/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold text-lg border border-emerald-200 dark:border-emerald-700/50">
                                {userVerifikator.name ? userVerifikator.name.charAt(0).toUpperCase() : <IconCircleCheck size={24} />}
                              </div>
                              <div>
                                <h4 className="font-bold text-base text-slate-800 dark:text-slate-100">
                                  {userVerifikator.name}
                                </h4>
                                {userVerifikator?.email && (
                                  <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {userVerifikator.email}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="px-2.5 py-1 text-[11px] font-bold rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                                {userVerifikator.roles?.map((r) => r.name.replace(/_/g, ' ').toUpperCase()).join(', ') || 'ADMIN PUSAT'}
                              </span>
                              <span className={`px-2.5 py-1 text-[11px] font-bold rounded-lg border ${
                                selectedPermohonan.status === 'disetujui'
                                  ? 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-700'
                                  : selectedPermohonan.status === 'ditolak'
                                  ? 'bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-700'
                                  : 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300'
                              }`}>
                                {selectedPermohonan.status === 'disetujui'
                                  ? '✓ Disetujui'
                                  : selectedPermohonan.status === 'ditolak'
                                  ? '✕ Ditolak'
                                  : selectedPermohonan.status === 'dibatalkan'
                                  ? 'Dibatalkan'
                                  : selectedPermohonan.status}
                              </span>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pt-4 text-xs">
                            <div className="space-y-1">
                              <span className="text-slate-400 text-[11px] block">Unit / Cabang Verifikator</span>
                              <div className="flex items-center gap-1.5 font-bold text-slate-800 dark:text-slate-200">
                                <IconBuildingStore size={15} className="text-emerald-500" />
                                <span>{userVerifikator.cabang?.nama_cabang || 'Kantor Pusat'}</span>
                              </div>
                            </div>

                            <div className="space-y-1">
                              <span className="text-slate-400 text-[11px] block">Waktu Verifikasi Selesai</span>
                              <div className="flex items-center gap-1.5 font-semibold text-slate-700 dark:text-slate-300">
                                <IconClock size={15} className="text-emerald-500" />
                                <span>{moment(selectedPermohonan.updated_at).format('DD MMM YYYY, HH:mm:ss')}</span>
                              </div>
                            </div>

                            <div className="space-y-1">
                              <span className="text-slate-400 text-[11px] block">Status Akun</span>
                              <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                                {userVerifikator.is_active !== false && userVerifikator.is_active !== 0 ? '● Aktif' : '○ Non-Aktif'}
                              </span>
                            </div>

                            <div className="space-y-1 sm:col-span-2 md:col-span-3 pt-2 border-t border-slate-200 dark:border-slate-600">
                              <span className="text-slate-400 text-[11px] block">Catatan / Keterangan Verifikator</span>
                              <div className="p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-medium leading-relaxed">
                                {((selectedPermohonan.catatan_verifikasi || selectedPermohonan.catatan_verifikator || selectedPermohonan.alasan_batal)
                                  ? (selectedPermohonan.catatan_verifikasi || selectedPermohonan.catatan_verifikator || selectedPermohonan.alasan_batal).replace(/^\[BATAL\]\s*/i, '')
                                  : null) || (
                                  <span className="italic text-slate-400">Tidak ada catatan atau instruksi khusus dari verifikator.</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : selectedPermohonan.status === 'dibatalkan' ? (
                        <div className="p-8 text-center bg-slate-50 dark:bg-slate-700/20 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-3">
                          <div className="w-12 h-12 mx-auto rounded-full bg-slate-200 dark:bg-slate-700 text-slate-500 flex items-center justify-center">
                            <IconAlertTriangle size={24} />
                          </div>
                          <h4 className="font-bold text-sm text-slate-800 dark:text-slate-100">Permohonan Telah Dibatalkan</h4>
                          <p className="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                            Permohonan ini telah dibatalkan sebelum diproses verifikasinya oleh Admin Pusat.
                          </p>
                          {(selectedPermohonan.catatan_verifikasi || selectedPermohonan.alasan_batal || selectedPermohonan.catatan_verifikator) && (
                            <div className="mt-3 p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-300 max-w-md mx-auto text-left">
                              <strong className="text-slate-500 block mb-1">Alasan Pembatalan:</strong>
                              {(selectedPermohonan.catatan_verifikasi || selectedPermohonan.alasan_batal || selectedPermohonan.catatan_verifikator).replace(/^\[BATAL\]\s*/i, '')}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="p-8 text-center bg-amber-50/50 dark:bg-amber-900/10 rounded-2xl border border-amber-200/60 dark:border-amber-900/30 space-y-3">
                          <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                            <IconClock size={24} />
                          </div>
                          <h4 className="font-bold text-sm text-amber-800 dark:text-amber-300">Menunggu Verifikator</h4>
                          <p className="text-xs text-slate-600 dark:text-slate-400 max-w-md mx-auto">
                            Permohonan ini berstatus <strong className="text-amber-700 dark:text-amber-400">Pending</strong> dan sedang dalam antrean pemeriksaan oleh tim Admin Pusat / Owner. Informasi detail akun verifikator akan muncul secara otomatis setelah dokumen disetujui atau ditolak.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* 5. Tab Galeri List Item */}
                {modalTab === 'galeri' && (
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="relative flex-1 max-w-xs">
                        <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                        <input
                          type="text"
                          placeholder="Cari kode, jenis, baki..."
                          value={searchModalItem}
                          onChange={(e) => setSearchModalItem(e.target.value)}
                          className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200"
                        />
                      </div>
                      <span className="text-[11px] text-slate-500 font-medium">
                        Menampilkan {filteredModalItems.length} foto item
                      </span>
                    </div>

                    {filteredModalItems.length === 0 ? (
                      <div className="py-12 text-center text-slate-400">
                        <IconInbox size={36} className="mx-auto mb-2 opacity-50" />
                        <p className="text-xs">Tidak ada item yang sesuai dengan pencarian</p>
                      </div>
                    ) : (
                      <PhotoProvider>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                          {filteredModalItems.map((item) => {
                            const imgUrl = resolveItemImageUrl(item);
                            return (
                              <div
                                key={item.id}
                                className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow group flex flex-col"
                              >
                                <div className="aspect-square bg-slate-100 dark:bg-slate-700/50 relative flex items-center justify-center overflow-hidden">
                                  {imgUrl ? (
                                    <PhotoView src={imgUrl}>
                                      <img
                                        src={imgUrl}
                                        alt={item.kode_item}
                                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    </PhotoView>
                                  ) : (
                                    <div className="flex flex-col items-center justify-center text-slate-400 p-2 text-center">
                                      <IconPhoto size={28} className="opacity-40 mb-1" />
                                      <span className="text-[10px]">Belum Ada Gambar</span>
                                    </div>
                                  )}
                                  <div className="absolute top-2 left-2 bg-slate-900/80 backdrop-blur-sm text-amber-400 text-[10px] font-mono font-bold px-2 py-0.5 rounded shadow">
                                    {item.kode_item}
                                  </div>
                                  <div className="absolute top-2 right-2 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow">
                                    {item.karat} K
                                  </div>
                                </div>

                                <div className="p-2.5 text-xs flex-1 flex flex-col justify-between space-y-2">
                                  <div>
                                    <p className="font-bold text-slate-800 dark:text-slate-100 text-[11px] truncate" title={`${item.jenis_kategori?.kategori?.nama_kategori || ''} - ${item.jenis_kategori?.nama_jenis || ''}`}>
                                      {item.jenis_kategori?.kategori?.nama_kategori || '-'} - {item.jenis_kategori?.nama_jenis || '-'}
                                    </p>
                                    <p className="text-[10px] text-slate-500 dark:text-slate-400">
                                      Baki: <span className="font-semibold text-slate-700 dark:text-slate-300">{item.nomor_rak || '-'}</span>
                                    </p>
                                  </div>
                                  <div className="flex items-center justify-between pt-1.5 border-t border-slate-100 dark:border-slate-700">
                                    <span className="text-[10px] text-slate-400">Berat</span>
                                    <span className="font-bold text-amber-600 dark:text-amber-400 text-xs">
                                      {parseFloat(item.berat || 0).toFixed(3)} gr
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </PhotoProvider>
                    )}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex justify-between items-center pt-3 border-t border-slate-100 dark:border-slate-700 flex-shrink-0">
                {selectedPermohonan.status === 'pending' ? (
                  <button
                    type="button"
                    onClick={() => handleOpenBatalModal(selectedPermohonan)}
                    className="px-3.5 py-2 text-xs font-bold text-rose-600 hover:text-white hover:bg-rose-600 border border-rose-200 dark:border-rose-800 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer shadow-sm"
                  >
                    <IconX size={15} />
                    <span>Batalkan Permohonan</span>
                  </button>
                ) : <div />}
                <button
                  type="button"
                  onClick={() => setIsOpenDetailModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-all cursor-pointer"
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Konfirmasi Batal Permohonan */}
        {isOpenBatalModal && permohonanToBatal && (
          <div className="fixed inset-0 z-[1000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !loadingBatal && setIsOpenBatalModal(false)}>
            <div className="bg-white dark:bg-slate-800 w-full max-w-md rounded-2xl p-6 shadow-2xl border border-slate-200 dark:border-slate-700 space-y-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-3 text-rose-600 dark:text-rose-400">
                <div className="p-3 bg-rose-50 dark:bg-rose-900/30 rounded-xl">
                  <IconAlertTriangle size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-base text-slate-800 dark:text-slate-100">Batalkan Permohonan</h3>
                  <p className="text-xs text-slate-500">Konfirmasi pembatalan pemindahan ke Brankas Pusat</p>
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-slate-700/40 p-3.5 rounded-xl space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400">No. Permohonan:</span>
                  <span className="font-mono font-bold text-amber-600 dark:text-amber-400">{permohonanToBatal.no_permohonan}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Total Barang:</span>
                  <span className="font-bold text-slate-700 dark:text-slate-200">
                    {permohonanToBatal.total_pcs || (permohonanToBatal.items ? permohonanToBatal.items.length : 0)} PCS ({parseFloat(permohonanToBatal.total_berat || (permohonanToBatal.items ? permohonanToBatal.items.reduce((acc, it) => acc + parseFloat(it.berat || 0), 0) : 0)).toFixed(3)} gr)
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Toko / Pemohon:</span>
                  <span className="font-semibold text-slate-700 dark:text-slate-200">
                    {permohonanToBatal.cabang_asal?.nama_cabang || permohonanToBatal.cabangAsal?.nama_cabang} ({permohonanToBatal.user_pemohon?.name || permohonanToBatal.userPemohon?.name})
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  Alasan Pembatalan (Opsional):
                </label>
                <textarea
                  value={alasanBatal}
                  onChange={(e) => setAlasanBatal(e.target.value)}
                  placeholder="Tuliskan alasan pembatalan permohonan..."
                  rows={2}
                  className="w-full text-xs p-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-rose-200 dark:focus:ring-rose-900/30 focus:border-rose-400 outline-none"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Barang dalam permohonan ini akan otomatis dikembalikan ke status <strong>Ready</strong> di Brankas Toko.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                <button
                  type="button"
                  disabled={loadingBatal}
                  onClick={() => {
                    setIsOpenBatalModal(false);
                    setPermohonanToBatal(null);
                    setAlasanBatal('');
                  }}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-all cursor-pointer"
                >
                  Kembali
                </button>
                <button
                  type="button"
                  disabled={loadingBatal}
                  onClick={handleExecuteBatal}
                  className="px-4 py-2 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-xl transition-all shadow-sm flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
                >
                  {loadingBatal ? (
                    <>
                      <span className="loading loading-spinner loading-xs"></span>
                      <span>Membatalkan...</span>
                    </>
                  ) : (
                    <>
                      <IconX size={16} />
                      <span>Ya, Batalkan Permohonan</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </Layouts>
  );
};

export default HistoriPermohonanBrankas;
